import { useCallback, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Bot,
  Wallet,
  Shield,
  Settings,
  Users,
  Activity,
  TrendingUp,
  Plus,
  LogOut,
  Search,
  BookOpen,
  FlaskConical,
  ScrollText,
  ClipboardList,
  Layers,
  Link2,
  Route,
  Puzzle,
  BarChart3,
  Flag,
  ShieldAlert,
} from 'lucide-react';
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from '@/shared/components/ui/command';
import { usePermissions } from '@/shared/hooks/use-permissions';
import { useCurrentUser } from '@/shared/hooks/use-current-user';
import { useBotsFleet } from '@/api/hooks/use-bots';
import { useVenueConnections } from '@/api/hooks/use-venue-connections';

interface CommandAction {
  label: string;
  icon: React.ElementType;
  action: () => void;
  shortcut?: string;
}

export function CommandPalette() {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { canAdmin, canCreate } = usePermissions();
  const { logout } = useCurrentUser();

  // Fetch bots and venue connections for dynamic search (uses cached data from polling)
  const { data: botsItems } = useBotsFleet((d) => d.items.slice(0, 100));
  const { data: connectionsData } = useVenueConnections({ limit: 100 });

  const matchingBots = useMemo(() => {
    const items = botsItems;
    if (!search || search.length < 2 || !items || items.length === 0) return [];
    const q = search.toLowerCase();
    return items.filter((b) => b.name.toLowerCase().includes(q)).slice(0, 5);
  }, [search, botsItems]);

  const matchingConnections = useMemo(() => {
    const items = connectionsData?.items;
    if (!search || search.length < 2 || !items || items.length === 0) return [];
    const q = search.toLowerCase();
    return items
      .filter(
        (a: { label?: string | null; venue?: string | null }) =>
          a.label?.toLowerCase().includes(q) || a.venue?.toLowerCase().includes(q),
      )
      .slice(0, 5);
  }, [search, connectionsData]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const go = useCallback(
    (path: string) => {
      navigate(path);
      setOpen(false);
    },
    [navigate],
  );

  const navItems: CommandAction[] = [
    { label: t('nav.dashboard'), icon: LayoutDashboard, action: () => go('/dashboard') },
    { label: t('nav.venueConnections'), icon: Link2, action: () => go('/venue-connections') },
    { label: t('nav.executionProfiles'), icon: Route, action: () => go('/execution-profiles') },
    { label: t('nav.bots'), icon: Bot, action: () => go('/bots') },
    { label: t('nav.strategies'), icon: Puzzle, action: () => go('/strategies') },
    { label: t('nav.wallets'), icon: Wallet, action: () => go('/wallets') },
    { label: t('nav.portfolio'), icon: TrendingUp, action: () => go('/portfolio') },
    { label: t('nav.orders'), icon: ClipboardList, action: () => go('/orders') },
    { label: t('nav.positions'), icon: Layers, action: () => go('/positions') },
    { label: t('nav.signals'), icon: Search, action: () => go('/signals') },
    { label: t('nav.risk'), icon: Shield, action: () => go('/risk') },
    { label: t('nav.safety'), icon: ShieldAlert, action: () => go('/safety'), shortcut: '⌘⇧H' },
    { label: t('nav.marketData'), icon: BarChart3, action: () => go('/market-data') },
    { label: t('nav.system'), icon: Activity, action: () => go('/system') },
    { label: t('nav.backtests'), icon: FlaskConical, action: () => go('/backtests') },
    { label: t('nav.settings'), icon: Settings, action: () => go('/settings') },
    { label: t('nav.glossary'), icon: BookOpen, action: () => go('/glossary') },
    ...(canAdmin
      ? [
          { label: t('nav.admin'), icon: Users, action: () => go('/admin') } as CommandAction,
          {
            label: t('nav.audit'),
            icon: ScrollText,
            action: () => go('/admin/audit'),
          } as CommandAction,
          {
            label: t('nav.featureFlags'),
            icon: Flag,
            action: () => go('/admin/feature-flags'),
          } as CommandAction,
          {
            label: t('nav.securityEvents'),
            icon: ShieldAlert,
            action: () => go('/admin/security-events'),
          } as CommandAction,
          {
            label: t('nav.dlq'),
            icon: Search,
            action: () => go('/admin/dlq'),
          } as CommandAction,
        ]
      : []),
  ];

  const actionItems: CommandAction[] = [
    ...(canCreate
      ? [
          {
            label: t('commandPalette.createBot'),
            icon: Plus,
            action: () => go('/bots/create'),
            shortcut: 'N',
          } as CommandAction,
        ]
      : []),
  ];

  const userItems: CommandAction[] = [
    {
      label: t('buttons.signOut'),
      icon: LogOut,
      action: () => {
        logout();
        setOpen(false);
      },
    },
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('commandPalette.searchPlaceholder')}
        className="group flex items-center gap-2 rounded-lg border border-line bg-bg-hover px-2.5 py-1.5 text-text-55 transition-colors hover:bg-bg-active hover:text-text lg:w-[260px] lg:justify-start"
      >
        <Search className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
        <span className="hidden text-[12.5px] text-left flex-1 truncate lg:inline">
          {t('commandPalette.searchPlaceholder')}
        </span>
        <kbd className="pointer-events-none hidden items-center gap-0.5 rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[10px] font-medium text-text-55 lg:inline-flex">
          <span className="text-[11px]">⌘</span>K
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setSearch('');
        }}
        showCloseButton={false}
      >
        <CommandInput
          placeholder={t('commandPalette.placeholder')}
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          <CommandEmpty>{t('commandPalette.noResults')}</CommandEmpty>

          {matchingBots.length > 0 && (
            <CommandGroup heading={t('nav.bots')}>
              {matchingBots.map((bot) => (
                <CommandItem key={`bot-${bot.id}`} onSelect={() => go(`/bots/${bot.id}`)}>
                  <Bot className="h-4 w-4" />
                  <span>{bot.name}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{bot.status}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {matchingConnections.length > 0 && (
            <CommandGroup heading={t('nav.venueConnections')}>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {matchingConnections.map((conn: any) => (
                <CommandItem
                  key={`conn-${conn.id}`}
                  onSelect={() => go(`/venue-connections/${conn.id}`)}
                >
                  <Link2 className="h-4 w-4" />
                  <span>{conn.label || conn.venue}</span>
                  <span className="ml-auto text-xs text-muted-foreground">{conn.venue}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {(matchingBots.length > 0 || matchingConnections.length > 0) && <CommandSeparator />}

          <CommandGroup heading={t('commandPalette.navigation')}>
            {navItems.map((item) => (
              <CommandItem key={item.label} onSelect={item.action}>
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>

          {actionItems.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading={t('commandPalette.actions')}>
                {actionItems.map((item) => (
                  <CommandItem key={item.label} onSelect={item.action}>
                    <item.icon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {item.shortcut && <CommandShortcut>{item.shortcut}</CommandShortcut>}
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <CommandSeparator />
          <CommandGroup heading={t('commandPalette.account')}>
            {userItems.map((item) => (
              <CommandItem key={item.label} onSelect={item.action}>
                <item.icon className="h-4 w-4" />
                <span>{item.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
